import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Search, Filter, AlertCircle, CheckCircle2, Clock, Plus, Play, TestTube, MessageSquare, Rocket, Pause, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { StatusDropdown } from '@/components/projects/StatusDropdown';
import { TaskDetailDialog } from '@/components/projects/TaskDetailDialog';
import { CreateTaskForm } from '@/components/projects/CreateTaskForm';
import taskService, { TaskApiResponse, TaskFilters } from '@/services/task.service';
import projectService from '@/services/project.service';
import { toast } from 'sonner';
import { useAuthStore } from '@/stores/authStore';
import { baseURL } from '@/lib/axios';
import { getStatusesByCategory } from '@/services/status.service';

interface Task {
  id: string;
  name: string;
  status: string;
  statusId?: string; // MongoDB ID of the status for API updates
  startDate: string;
  dueDate: string;
  assignedTo: {
    name: string;
    avatar?: string | null;
    initials: string;
  };
  assignees?: Array<{
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    profilePicture?: string | null;
    initials?: string;
  }>;
  followers?: Array<{
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  }>;
  tags: string[];
  priority: string;
  estimatedHours: number;
  description?: string;
  url?: string;
  projectId?: string;
  projectName?: string;
}

const apiBaseWithoutPrefix = baseURL ? baseURL.replace(/\/api\/v1\/?$/, '') : '';

const buildImageUrlIfNeeded = (path?: string | null) => {
  if (!path) return null;
  if (path.startsWith('http')) {
    return path;
  }
  if (!apiBaseWithoutPrefix) {
    return path;
  }
  const normalizedBase = apiBaseWithoutPrefix.replace(/\/$/, '');
  const normalizedPath = path.startsWith('/') ? path.substring(1) : path;
  return `${normalizedBase}/${normalizedPath}`;
};

// Helper function to generate initials from name
const getInitials = (firstName?: string, lastName?: string, email?: string): string => {
  if (!firstName && !lastName) {
    if (email) {
      return email.slice(0, 2).toUpperCase();
    }
    return 'UN'; // Unassigned
  }
  
  const first = firstName?.charAt(0) || '';
  const last = lastName?.charAt(0) || '';
  
  if (first && last) {
    return `${first}${last}`.toUpperCase();
  }
  
  // If only one name is available, use first two letters
  const name = firstName || lastName || '';
  return name.slice(0, 2).toUpperCase() || 'UN';
};

// Helper function to convert API response to UI Task format
const convertApiTaskToUITask = (apiTask: TaskApiResponse): Task => {
  const firstAssignee = apiTask.assignees && apiTask.assignees.length > 0 
    ? apiTask.assignees[0] 
    : null;
  
  // Safely get assignee name
  const getAssigneeName = () => {
    if (!firstAssignee) return 'Unassigned';
    
    const firstName = firstAssignee.firstName || '';
    const lastName = firstAssignee.lastName || '';
    const email = firstAssignee.email || '';
    
    if (firstName || lastName) {
      return `${firstName} ${lastName}`.trim();
    }
    
    if (email) {
      return email; // Fallback to email if no name
    }
    
    return 'Unassigned';
  };
  
  // Get project name from multiple possible sources
  const getProjectName = () => {
    // First check if projectName is directly on the task
    if (apiTask.projectName) {
      return apiTask.projectName;
    }
    // Then check if project is an object with projectName
    if (apiTask.project && typeof apiTask.project === 'object' && apiTask.project.projectName) {
      return apiTask.project.projectName;
    }
    // Fallback to empty string
    return '';
  };

  // Extract status from API response - can be string (ID), object (populated), or undefined
  const getStatus = () => {
    if (!apiTask.status) return null;
    
    // If status is an object (populated), use the name
    if (typeof apiTask.status === 'object' && 'name' in apiTask.status) {
      return {
        id: apiTask.status._id,
        name: apiTask.status.name,
      };
    }
    
    // If status is a string, it could be an ID or a name
    // We'll return it as-is and let the StatusDropdown match it
    return {
      id: apiTask.status,
      name: apiTask.status, // Will be matched by StatusDropdown
    };
  };

  const statusInfo = getStatus();

  return {
    id: apiTask._id,
    name: apiTask.title,
    status: statusInfo?.name || statusInfo?.id || 'NOT_STARTED',
    statusId: statusInfo?.id, // Store the status ID separately for API updates
    startDate: apiTask.startDate,
    dueDate: apiTask.dueDate || '',
    assignedTo: {
      name: getAssigneeName(),
      initials: getInitials(firstAssignee?.firstName, firstAssignee?.lastName, firstAssignee?.email),
      avatar: buildImageUrlIfNeeded(firstAssignee?.profilePicture),
    },
    assignees: (apiTask.assignees || []).map((assignee: any) => ({
      ...assignee,
      initials: getInitials(assignee.firstName, assignee.lastName, assignee.email),
    })),
    followers: apiTask.followers,
    tags: apiTask.tags || [],
    priority: apiTask.priority,
    estimatedHours: apiTask.estimateHours,
    description: apiTask.description,
    url: apiTask.url,
    projectId: apiTask.projectId || (typeof apiTask.project === 'string' ? apiTask.project : apiTask.project?._id || ''),
    projectName: getProjectName(),
  };
};

const getPriorityColor = (priority: string) => {
  switch (priority) {
    case 'High':
      return 'bg-red-100 text-red-800 border-red-200';
    case 'Medium':
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case 'Low':
      return 'bg-green-100 text-green-800 border-green-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

const formatDate = (dateString: string): string => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return dateString;
  }
};

const isOverdue = (dueDate: string): boolean => {
  if (!dueDate) return false;
  try {
    const due = new Date(dueDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    due.setHours(0, 0, 0, 0);
    return due < today;
  } catch {
    return false;
  }
};

export default function Tasks() {
  const { hasSubModulePermission } = useAuthStore();
  
  // State
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [projectFilter, setProjectFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [isTaskDetailOpen, setIsTaskDetailOpen] = useState(false);
  const [isCreateTaskOpen, setIsCreateTaskOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [projects, setProjects] = useState<Array<{ _id: string; projectName: string }>>([]);
  const [totalTasks, setTotalTasks] = useState(0);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const itemsPerPage = 20;
  const [taskStatuses, setTaskStatuses] = useState<Array<{ name: string; id: string; isActive: boolean }>>([]);
  const [taskToDelete, setTaskToDelete] = useState<Task | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  // Fetch task statuses from API for filter dropdown
  useEffect(() => {
    const fetchTaskStatuses = async () => {
      try {
        const statuses = await getStatusesByCategory('task');
        setTaskStatuses(statuses.map(s => ({ name: s.name, id: s.id, isActive: s.isActive })));
      } catch (error) {
        console.error('Error fetching task statuses:', error);
      }
    };
    fetchTaskStatuses();
  }, []);

  // Fetch projects for filter dropdown
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await projectService.getProjects({});
        const projectsList = response.data || [];
        setProjects(projectsList.map((p: any) => ({
          _id: p._id || p.id,
          projectName: p.projectName || p.name
        })));
      } catch (error) {
        console.error('Error fetching projects:', error);
      }
    };
    fetchProjects();
  }, []);

  // Fetch tasks from API
  useEffect(() => {
    const fetchTasks = async () => {
      setIsLoading(true);
      try {
        const filters: TaskFilters = {
          page: currentPage,
          limit: itemsPerPage,
        };
        
        if (searchTerm) {
          filters.search = searchTerm;
        }
        
        if (statusFilter !== 'all') {
          // Find the status ID from the status name
          const selectedStatus = taskStatuses.find(s => 
            s.name.toUpperCase() === statusFilter.toUpperCase()
          );
          // Use status ID if found, otherwise use the filter value as-is
          filters.status = selectedStatus?.id || statusFilter;
        }
        
        if (projectFilter !== 'all') {
          filters.projectId = projectFilter;
        }
        
        const response = await taskService.getAllTasks(filters);
        
        if (response && response.data) {
          const uiTasks = response.data.map(convertApiTaskToUITask);
          setTasks(uiTasks);
          setTotalTasks(response.total || 0);
          // response.hasMore unused for now
        } else {
          setTasks([]);
          setTotalTasks(0);
          // response.hasMore unused for now
        }
      } catch (error: any) {
        console.error('Error fetching tasks:', error);
        const errorMessage = error.response?.data?.message || error.message || 'Failed to load tasks';
        toast.error(errorMessage);
        setTasks([]);
        setTotalTasks(0);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTasks();
  }, [searchTerm, statusFilter, projectFilter, currentPage, refreshTrigger, taskStatuses]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, projectFilter]);

  // State for all tasks (for statistics calculation)
  const [allTasksForStats, setAllTasksForStats] = useState<Task[]>([]);

  // Fetch all tasks for statistics (without pagination)
  useEffect(() => {
    const fetchAllTasksForStats = async () => {
      try {
        const filters: TaskFilters = {
          limit: 1000, // Fetch a large number to get all tasks for stats
        };
        
        // Apply search and filters but not pagination
        if (searchTerm) {
          filters.search = searchTerm;
        }
        
        if (statusFilter !== 'all') {
          const selectedStatus = taskStatuses.find(s => 
            s.name.toUpperCase() === statusFilter.toUpperCase()
          );
          filters.status = selectedStatus?.id || statusFilter;
        }
        
        if (projectFilter !== 'all') {
          filters.projectId = projectFilter;
        }
        
        const response = await taskService.getAllTasks(filters);
        
        if (response && response.data) {
          const uiTasks = response.data.map(convertApiTaskToUITask);
          setAllTasksForStats(uiTasks);
        } else {
          setAllTasksForStats([]);
        }
      } catch (error) {
        console.error('Error fetching tasks for statistics:', error);
        setAllTasksForStats([]);
      }
    };

    fetchAllTasksForStats();
  }, [searchTerm, statusFilter, projectFilter, refreshTrigger, taskStatuses]);

  // Helper function to normalize status for comparison
  const normalizeStatusForComparison = (status: string): string => {
    if (!status || status.trim() === '') return 'UNKNOWN';
    const statusUpper = status.toUpperCase().trim();
    
    // Map common status variations to standard values
    const statusMap: Record<string, string> = {
      'NOT_STARTED': 'NOT_STARTED',
      'NOT STARTED': 'NOT_STARTED',
      'IN_PROGRESS': 'IN_PROGRESS',
      'IN PROGRESS': 'IN_PROGRESS',
      'TESTING': 'TESTING',
      'AWAITING_FEEDBACK': 'AWAITING_FEEDBACK',
      'AWAITING FEEDBACK': 'AWAITING_FEEDBACK',
      'READY_FOR_PRODUCTION': 'READY_FOR_PRODUCTION',
      'READY FOR PRODUCTION': 'READY_FOR_PRODUCTION',
      'ON_HOLD': 'ON_HOLD',
      'ON HOLD': 'ON_HOLD',
      'COMPLETE': 'COMPLETE',
      'COMPLETED': 'COMPLETE',
      'UNDER_REVIEWING': 'UNDER_REVIEWING',
      'UNDER REVIEWING': 'UNDER_REVIEWING',
      'UNDER_REVIEW': 'UNDER_REVIEWING',
      'UNDER REVIEW': 'UNDER_REVIEWING',
    };
    
    return statusMap[statusUpper] || statusUpper;
  };

  // Calculate statistics for all task statuses
  const stats = useMemo(() => {
    const total = totalTasks;
    
    // Use allTasksForStats for accurate counts, fallback to tasks if not available
    const tasksForStats = allTasksForStats.length > 0 ? allTasksForStats : tasks;
    
    // Debug: Log all unique statuses to help identify issues
    if (tasksForStats.length > 0) {
      const uniqueStatuses = [...new Set(tasksForStats.map(t => t.status))];
      console.log('[Tasks Stats] All unique statuses found:', uniqueStatuses);
      const normalizedStatuses = uniqueStatuses.map(s => ({
        original: s,
        normalized: normalizeStatusForComparison(s)
      }));
      console.log('[Tasks Stats] Normalized statuses:', normalizedStatuses);
    }
    
    const notStarted = tasksForStats.filter(t => {
      if (!t.status || t.status.trim() === '') return false; // Don't count empty statuses
      const normalized = normalizeStatusForComparison(t.status);
      return normalized === 'NOT_STARTED';
    }).length;
    
    const inProgress = tasksForStats.filter(t => {
      if (!t.status || t.status.trim() === '') return false;
      const normalized = normalizeStatusForComparison(t.status);
      return normalized === 'IN_PROGRESS';
    }).length;
    
    const testing = tasksForStats.filter(t => {
      if (!t.status || t.status.trim() === '') return false;
      const normalized = normalizeStatusForComparison(t.status);
      return normalized === 'TESTING';
    }).length;
    
    const awaitingFeedback = tasksForStats.filter(t => {
      if (!t.status || t.status.trim() === '') return false;
      const normalized = normalizeStatusForComparison(t.status);
      return normalized === 'AWAITING_FEEDBACK';
    }).length;
    
    const readyForProduction = tasksForStats.filter(t => {
      if (!t.status || t.status.trim() === '') return false;
      const normalized = normalizeStatusForComparison(t.status);
      return normalized === 'READY_FOR_PRODUCTION';
    }).length;
    
    const onHold = tasksForStats.filter(t => {
      if (!t.status || t.status.trim() === '') return false;
      const normalized = normalizeStatusForComparison(t.status);
      return normalized === 'ON_HOLD';
    }).length;
    
    const completed = tasksForStats.filter(t => {
      if (!t.status || t.status.trim() === '') return false;
      const normalized = normalizeStatusForComparison(t.status);
      return normalized === 'COMPLETE';
    }).length;
    
    console.log('[Tasks Stats] Calculated counts:', {
      total,
      notStarted,
      inProgress,
      testing,
      awaitingFeedback,
      readyForProduction,
      onHold,
      completed,
      tasksCount: tasksForStats.length
    });
    
    return { 
      total, 
      notStarted, 
      inProgress, 
      testing, 
      awaitingFeedback, 
      readyForProduction, 
      onHold, 
      completed 
    };
  }, [allTasksForStats, tasks, totalTasks]);

  const handleTaskClick = (task: Task) => {
    setSelectedTask(task);
    setIsTaskDetailOpen(true);
  };

  const handleTaskDetailClose = () => {
    setIsTaskDetailOpen(false);
    setSelectedTask(null);
  };

  const handleStatusChange = async (taskId: string, statusId: string, statusName: string) => {
    try {
      // API expects MongoDB ID, not status name
      await taskService.updateTaskStatus(taskId, statusId);
      toast.success('Task status updated successfully');
      
      // Update local state with status name for display
      setTasks(prevTasks =>
        prevTasks.map(task =>
          task.id === taskId ? { ...task, status: statusName } : task
        )
      );
      
      // Update allTasksForStats to reflect the status change immediately
      setAllTasksForStats(prevTasks =>
        prevTasks.map(task =>
          task.id === taskId ? { ...task, status: statusName } : task
        )
      );
      
      if (selectedTask && selectedTask.id === taskId) {
        setSelectedTask({ ...selectedTask, status: statusName });
      }
      
      // Trigger refresh to ensure statistics are recalculated
      setRefreshTrigger(prev => prev + 1);
    } catch (error: any) {
      console.error('Error updating task status:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update task status';
      toast.error(errorMessage);
    }
  };

  const handleDeleteClick = (task: Task, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row click
    setTaskToDelete(task);
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!taskToDelete) return;

    try {
      await taskService.deleteTask(taskToDelete.id);
      toast.success('Task deleted successfully');
      
      // Remove task from local state
      setTasks(prevTasks => prevTasks.filter(task => task.id !== taskToDelete.id));
      setTotalTasks(prev => Math.max(0, prev - 1));
      
      // Close task detail if it was the deleted task
      if (selectedTask && selectedTask.id === taskToDelete.id) {
        setIsTaskDetailOpen(false);
        setSelectedTask(null);
      }
      
      setIsDeleteDialogOpen(false);
      setTaskToDelete(null);
    } catch (error: any) {
      console.error('Error deleting task:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete task';
      toast.error(errorMessage);
      setIsDeleteDialogOpen(false);
      setTaskToDelete(null);
    }
  };

  const totalPages = Math.ceil(totalTasks / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Tasks</h1>
          <p className="text-muted-foreground">Manage and track your tasks across all projects</p>
        </div>
        <div className="flex gap-2">
          {hasSubModulePermission('project_management', 'task', 'create') && (
            <Button onClick={() => setIsCreateTaskOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Task
            </Button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <CheckCircle2 className="h-6 w-6 text-blue-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600 mb-1">Total Tasks</p>
                <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
                <p className="text-xs text-muted-foreground mt-1">Across all projects</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                <Play className="h-6 w-6 text-gray-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600 mb-1">Not Started</p>
                <p className="text-3xl font-bold text-gray-900">{stats.notStarted}</p>
                <p className="text-xs text-muted-foreground mt-1">Tasks not started</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                <Clock className="h-6 w-6 text-indigo-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600 mb-1">In Progress</p>
                <p className="text-3xl font-bold text-gray-900">{stats.inProgress}</p>
                <p className="text-xs text-muted-foreground mt-1">Currently being worked on</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <TestTube className="h-6 w-6 text-purple-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600 mb-1">Testing</p>
                <p className="text-3xl font-bold text-gray-900">{stats.testing}</p>
                <p className="text-xs text-muted-foreground mt-1">Tasks in testing</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <MessageSquare className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600 mb-1">Awaiting Feedback</p>
                <p className="text-3xl font-bold text-gray-900">{stats.awaitingFeedback}</p>
                <p className="text-xs text-muted-foreground mt-1">Waiting for feedback</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center">
                <Rocket className="h-6 w-6 text-cyan-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600 mb-1">Ready for Production</p>
                <p className="text-3xl font-bold text-gray-900">{stats.readyForProduction}</p>
                <p className="text-xs text-muted-foreground mt-1">Ready to deploy</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <Pause className="h-6 w-6 text-orange-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600 mb-1">On Hold</p>
                <p className="text-3xl font-bold text-gray-900">{stats.onHold}</p>
                <p className="text-xs text-muted-foreground mt-1">Tasks on hold</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle2 className="h-6 w-6 text-green-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600 mb-1">Completed</p>
                <p className="text-3xl font-bold text-gray-900">{stats.completed}</p>
                <p className="text-xs text-muted-foreground mt-1">Finished tasks</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Filter className="h-5 w-5" />
            <span>Filter & Search Tasks</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search tasks..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {taskStatuses
                  .filter(status => status.isActive)
                  .map((status) => (
                    <SelectItem key={status.id} value={status.name.toUpperCase()}>
                      {status.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            <Select value={projectFilter} onValueChange={setProjectFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by project" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Projects</SelectItem>
                {projects.map((project) => (
                  <SelectItem key={project._id} value={project._id}>
                    {project.projectName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('all');
                setProjectFilter('all');
              }}
            >
              Clear Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tasks Table */}
      <Card>
        <CardHeader>
          <CardTitle>Tasks ({totalTasks})</CardTitle>
          <CardDescription>
            Detailed view of all tasks across projects
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]" role="status">
                  <span className="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]">Loading...</span>
                </div>
                <p className="mt-2 text-sm text-gray-500">Loading tasks...</p>
              </div>
            </div>
          ) : tasks.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <p className="text-gray-500">No tasks found</p>
                <p className="text-sm text-gray-400 mt-1">
                  {searchTerm || statusFilter !== 'all' || projectFilter !== 'all'
                    ? 'Try adjusting your filters'
                    : 'Create a task to get started'}
                </p>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>#</TableHead>
                    <TableHead>Task Name</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Start Date</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead>Assigned To</TableHead>
                    <TableHead>Tags</TableHead>
                    <TableHead>Priority</TableHead>
                    <TableHead>Est. Hours</TableHead>
                    {hasSubModulePermission('project_management', 'task', 'delete') && (
                      <TableHead>Actions</TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tasks.map((task, index) => (
                    <TableRow 
                      key={task.id}
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => handleTaskClick(task)}
                    >
                      <TableCell className="font-medium">
                        {String(startIndex + index + 1).padStart(2, '0')}
                      </TableCell>
                      <TableCell className="max-w-xs">
                        <div className="truncate font-medium">{task.name}</div>
                        {task.projectName && (
                          <div className="text-xs text-blue-600 mt-1 font-medium">{task.projectName}</div>
                        )}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <StatusDropdown
                          currentStatus={task.status}
                          onStatusChange={(statusId, statusName) => handleStatusChange(task.id, statusId, statusName)}
                        />
                      </TableCell>
                      <TableCell className="text-sm text-gray-600">
                        {formatDate(task.startDate)}
                      </TableCell>
                      <TableCell className="text-sm text-gray-600">
                        <div className="flex items-center space-x-1">
                          <span>{formatDate(task.dueDate)}</span>
                          {isOverdue(task.dueDate) && (
                            <AlertCircle className="h-4 w-4 text-red-500" />
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {task.assignees && task.assignees.length > 0 ? (
                          <div className="flex flex-wrap items-center gap-2">
                            {task.assignees.slice(0, 3).map((assignee) => {
                              const imageUrl = assignee.profilePicture ? buildImageUrlIfNeeded(assignee.profilePicture) : null;
                              const initials = assignee.initials || getInitials(assignee.firstName, assignee.lastName, assignee.email);
                              const displayName = `${assignee.firstName ?? ''} ${assignee.lastName ?? ''}`.trim() || assignee.email || 'Assignee';
                              return (
                                <div
                                  key={assignee._id}
                                  className="relative h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-xs font-medium text-blue-800 overflow-hidden"
                                  title={displayName}
                                >
                                  {imageUrl ? (
                                    <img
                                      src={imageUrl}
                                      alt={displayName}
                                      className="h-full w-full object-cover"
                                      onError={(e) => {
                                        e.currentTarget.style.display = 'none';
                                        e.currentTarget.nextElementSibling?.classList.remove('hidden');
                                      }}
                                    />
                                  ) : null}
                                  <span className={`${imageUrl ? 'hidden' : ''}`}>
                                    {initials}
                                  </span>
                                </div>
                              );
                            })}
                            {task.assignees.length > 3 && (
                              <span className="text-xs text-gray-500">+{task.assignees.length - 3}</span>
                            )}
                          </div>
                        ) : (
                          <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center text-xs font-medium text-gray-600">
                            {task.assignedTo.initials}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {task.tags.slice(0, 2).map((tag) => (
                            <Badge key={tag} variant="secondary" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                          {task.tags.length > 2 && (
                            <Badge variant="secondary" className="text-xs">
                              +{task.tags.length - 2}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={cn('px-2 py-1 text-xs', getPriorityColor(task.priority))}>
                          {task.priority}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-gray-600">
                        {task.estimatedHours}h
                      </TableCell>
                      {hasSubModulePermission('project_management', 'task', 'delete') && (
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={(e) => handleDeleteClick(task, e)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Pagination */}
          {!isLoading && tasks.length > 0 && totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <div className="text-sm text-gray-600">
                Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, totalTasks)} of {totalTasks} tasks
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                <div className="flex items-center space-x-1">
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    let page;
                    if (totalPages <= 5) {
                      page = i + 1;
                    } else if (currentPage <= 3) {
                      page = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      page = totalPages - 4 + i;
                    } else {
                      page = currentPage - 2 + i;
                    }
                    return (
                      <Button
                        key={page}
                        variant={currentPage === page ? "default" : "outline"}
                        size="sm"
                        onClick={() => setCurrentPage(page)}
                        className="w-8 h-8 p-0"
                      >
                        {page}
                      </Button>
                    );
                  })}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Task Detail Dialog */}
      <TaskDetailDialog
        open={isTaskDetailOpen}
        onOpenChange={handleTaskDetailClose}
        task={selectedTask}
        onTaskUpdate={async (taskId, updates) => {
          console.log('Updating task:', taskId, updates);
          
          try {
            // Refetch the task to get complete data including profile pictures
            const updatedTaskApi = await taskService.getTaskById(taskId);
            const updatedTask = convertApiTaskToUITask(updatedTaskApi);
            
            // Update the task list with the refetched task data
            setTasks(prevTasks =>
              prevTasks.map(task =>
                task.id === taskId ? updatedTask : task
              )
            );
            
            // Update allTasksForStats to reflect the update immediately
            setAllTasksForStats(prevTasks =>
              prevTasks.map(task =>
                task.id === taskId ? updatedTask : task
              )
            );
            
            // Update selectedTask if it's the one being updated
            if (selectedTask && selectedTask.id === taskId) {
              setSelectedTask(updatedTask);
            }
            
            // Trigger refresh to ensure statistics are recalculated
            setRefreshTrigger(prev => prev + 1);
          } catch (error) {
            console.error('Error refetching task after update:', error);
            // Fallback to the original update logic if refetch fails
            setTasks(prevTasks =>
              prevTasks.map(task =>
                task.id === taskId ? { ...task, ...updates } : task
              )
            );
            
            // Update allTasksForStats with fallback data
            setAllTasksForStats(prevTasks =>
              prevTasks.map(task =>
                task.id === taskId ? { ...task, ...updates } : task
              )
            );
            
            if (selectedTask && selectedTask.id === taskId) {
              setSelectedTask({ ...selectedTask, ...updates });
            }
            
            // Trigger refresh
            setRefreshTrigger(prev => prev + 1);
          }
        }}
      />

      {/* Create Task Dialog */}
      <CreateTaskForm
        open={isCreateTaskOpen}
        onOpenChange={setIsCreateTaskOpen}
        onTaskCreated={(_task) => {
          setIsCreateTaskOpen(false);
          // Refresh tasks list
          setCurrentPage(1);
          setRefreshTrigger(prev => prev + 1);
          toast.success('Task created successfully');
        }}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Task</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the task "{taskToDelete?.name}"?
              <br /><br />
              This action cannot be undone and will permanently remove the task.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setIsDeleteDialogOpen(false);
              setTaskToDelete(null);
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              Delete Task
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
